import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader, KPICard } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Megaphone, Send, Users, Eye, Plus } from "lucide-react";
import { toast } from "sonner";

const AUDIENCE_LABEL = {
  ALL_MEMBERS: "All members",
  AT_RISK: "At-risk (<60% attendance)",
  EXPIRING_30: "Expiring in 30 days",
  EXPIRING_15: "Expiring in 15 days",
  EXPIRING_7: "Expiring in 7 days",
  CHURNED: "Churned (no visit 30+ days)",
};

export default function MarketingPage() {
  const { activeBranchId } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [previewAudience, setPreviewAudience] = useState("ALL_MEMBERS");

  const { data: campaigns = [] } = useQuery({
    queryKey: ["campaigns"],
    queryFn: async () => (await apiClient.get("/marketing/campaigns")).data,
  });

  const { data: preview } = useQuery({
    queryKey: ["audience-preview", previewAudience, activeBranchId],
    queryFn: async () => (await apiClient.get("/marketing/audience-preview", {
      params: { audience: previewAudience, ...(activeBranchId ? { branch_id: activeBranchId } : {}) },
    })).data,
  });

  const send = useMutation({
    mutationFn: async (id) => (await apiClient.post(`/marketing/campaigns/${id}/send`)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success(`Campaign sent — ${d.success_count}/${d.recipient_count} delivered`);
    },
  });

  const create = useMutation({
    mutationFn: async (body) => (await apiClient.post("/marketing/campaigns", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success("Campaign created");
      setOpen(false);
    },
  });

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="marketing-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold flex items-center gap-2">
            <Megaphone size={12} /> Marketing
          </div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Broadcast</h1>
          <div className="text-zinc-500 text-sm mt-1">Send WhatsApp campaigns to targeted segments via Gupshup.</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-campaign-button" className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
              <Plus size={16} className="mr-1" /> New Campaign
            </Button>
          </DialogTrigger>
          <NewCampaignDialog onSubmit={(b) => create.mutate(b)} />
        </Dialog>
      </div>

      {/* Audience preview */}
      <Panel>
        <PanelHeader title="Audience Preview" subtitle="Check segment size before you broadcast" />
        <div className="flex flex-wrap items-center gap-3">
          {Object.entries(AUDIENCE_LABEL).map(([k, v]) => (
            <button
              key={k}
              onClick={() => setPreviewAudience(k)}
              data-testid={`audience-${k}`}
              className={`text-[10px] uppercase tracking-[0.16em] px-3 py-2 rounded-sm border transition-colors ${
                previewAudience === k ? "border-volt text-volt bg-volt/10" : "border-zinc-800 hover:border-zinc-600 text-zinc-300"
              }`}>
              {v}
            </button>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Recipients</div>
            <div className="font-display text-5xl font-black text-volt mt-1" data-testid="audience-count">{preview?.count ?? "—"}</div>
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold mb-2">Sample</div>
            <div className="flex flex-wrap gap-2">
              {(preview?.sample || []).slice(0, 6).map((s) => (
                <span key={s.phone || s.name} className="text-xs bg-zinc-950 border border-zinc-900 px-2 py-1 rounded-sm">
                  {s.name} · <span className="text-zinc-500">{s.phone}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </Panel>

      <Panel>
        <PanelHeader title="Campaigns" subtitle="History" />
        {campaigns.length === 0 ? (
          <div className="text-zinc-600 text-sm py-12 text-center">No campaigns yet. Click "New Campaign".</div>
        ) : (
          <div className="space-y-2">
            {campaigns.map((c) => (
              <div key={c.id} data-testid={`campaign-${c.id}`} className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{c.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                    {AUDIENCE_LABEL[c.audience]} · template: <span className="font-mono-cmd text-volt">{c.template}</span>
                  </div>
                  {c.status === "SENT" && (
                    <div className="text-xs text-zinc-400 mt-1">
                      Sent to {c.success_count}/{c.recipient_count} on {new Date(c.sent_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                  )}
                </div>
                <span className="text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-1 rounded-sm"
                  style={{
                    background: c.status === "SENT" ? "#00E67622" : "#FFEA0022",
                    color: c.status === "SENT" ? "#00E676" : "#FFEA00",
                  }}>
                  {c.status}
                </span>
                {c.status !== "SENT" && (
                  <Button size="sm" onClick={() => send.mutate(c.id)} disabled={send.isPending} data-testid={`send-campaign-${c.id}`}
                    className="bg-volt hover:bg-volt-hover text-black font-bold uppercase">
                    <Send size={12} className="mr-1" /> Send Now
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function NewCampaignDialog({ onSubmit }) {
  const { activeBranchId } = useAuth();
  const [f, setF] = useState({
    name: "", audience: "AT_RISK", branch_id: activeBranchId || "",
    template: "gym_winback", params_csv: "",
  });
  return (
    <DialogContent className="bg-[#0a0a0a] border-zinc-800 text-white max-w-lg" data-testid="new-campaign-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl uppercase tracking-tight">New Campaign</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Campaign name</Label>
          <Input data-testid="campaign-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Audience</Label>
          <Select value={f.audience} onValueChange={(v) => setF({ ...f, audience: v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="campaign-audience"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(AUDIENCE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">WhatsApp template name</Label>
          <Input data-testid="campaign-template" value={f.template} onChange={(e) => setF({ ...f, template: e.target.value })}
            placeholder="e.g. gym_winback, gym_renewal_reminder" className="bg-zinc-950 border-zinc-800 mt-1 font-mono-cmd" />
        </div>
        <Button onClick={() => f.name && f.template ? onSubmit(f) : toast.error("Name & template required")}
          data-testid="submit-campaign" className="w-full bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider">
          Create Campaign
        </Button>
      </div>
    </DialogContent>
  );
}
